import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendar, faUser, faClock, faEuroSign, faPhone, faEnvelope, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useEmployeeServices } from '@/features/services/hooks/useEmployeeServices';
import { bookingsWriteApi } from '@/api/api';
import { AppBookingCheckRequest, AppCreateBookingRequest } from '@/api/generated';

interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFinish?: (bookingData: any) => void; // Callback pro dokončení rezervace
  selectedDate: string;
  salonId: string;
  staffList: Array<{ id: string; name: string }>;
  servicesList: Array<{ id: string; name: string; price: number; duration: number }>;
  preSelectedTime?: string;
  preSelectedStaff?: string;
  preSelectedCustomer?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  };
}

const CreateBookingModal: React.FC<CreateBookingModalProps> = ({
  isOpen,
  onClose,
  onFinish,
  selectedDate,
  salonId,
  staffList,
  servicesList,
  preSelectedTime,
  preSelectedStaff,
  preSelectedCustomer,
}) => {
  const [formData, setFormData] = useState({
    customerFirstName: preSelectedCustomer?.firstName || '',
    customerLastName: preSelectedCustomer?.lastName || '',
    customerPhone: preSelectedCustomer?.phone || '',
    customerEmail: preSelectedCustomer?.email || '',
    selectedStaff: preSelectedStaff || '',
    selectedService: '',
    selectedTime: preSelectedTime || '',
    notes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityCheck, setAvailabilityCheck] = useState<{
    isAvailable: boolean | null;
    isLoading: boolean;
    message: string;
  }>({
    isAvailable: null,
    isLoading: false,
    message: '',
  });

  // Načítání služeb podle vybraného zaměstnance
  const { services: employeeServices, isLoading: isLoadingEmployeeServices } = useEmployeeServices(formData.selectedStaff || null);

  // Aktualizace stavu při změně preSelectedTime nebo preSelectedStaff nebo preSelectedCustomer
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      customerFirstName: preSelectedCustomer?.firstName || prev.customerFirstName,
      customerLastName: preSelectedCustomer?.lastName || prev.customerLastName,
      customerPhone: preSelectedCustomer?.phone || prev.customerPhone,
      customerEmail: preSelectedCustomer?.email || prev.customerEmail,
      selectedStaff: preSelectedStaff || prev.selectedStaff,
      selectedTime: preSelectedTime || prev.selectedTime,
    }));
  }, [preSelectedTime, preSelectedStaff, preSelectedCustomer]);

  // Generujeme časové sloty pro vybraný den (8:00 - 20:00 po 30 minutách)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 8; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value,
      };
      
      // Resetujeme vybranou službu při změně zaměstnance
      if (name === 'selectedStaff') {
        newData.selectedService = '';
      }
      
      return newData;
    });
  };

  // Kontrola dostupnosti rezervace
  const checkAvailability = async () => {
    if (!formData.selectedStaff || !formData.selectedService || !formData.selectedTime) {
      setAvailabilityCheck({
        isAvailable: null,
        isLoading: false,
        message: '',
      });
      return;
    }

    setAvailabilityCheck(prev => ({ ...prev, isLoading: true }));

    try {
      const checkRequest: AppBookingCheckRequest = {
        salonId,
        workerId: formData.selectedStaff,
        serviceId: formData.selectedService,
        date: selectedDate,
        startTime: formData.selectedTime,
      };

      const response = await bookingsWriteApi.checkBookingAvailability({ appBookingCheckRequest: checkRequest });
      
      setAvailabilityCheck({
        isAvailable: response.data.isAvailable,
        isLoading: false,
        message: response.data.isAvailable 
          ? 'Rezervace je dostupná' 
          : 'Rezervace není dostupná v tomto čase',
      });
    } catch (error) {
      console.error('Chyba při kontrole dostupnosti:', error);
      setAvailabilityCheck({
        isAvailable: false,
        isLoading: false,
        message: 'Chyba při kontrole dostupnosti',
      });
    }
  };

  // Kontrola dostupnosti při změně času, zaměstnance nebo služby
  useEffect(() => {
    if (formData.selectedStaff && formData.selectedService && formData.selectedTime) {
      checkAvailability();
    }
  }, [formData.selectedStaff, formData.selectedService, formData.selectedTime, selectedDate, salonId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!availabilityCheck.isAvailable) {
      alert('Rezervace není dostupná v tomto čase');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const createRequest: AppCreateBookingRequest = {
        salonId,
        workerId: formData.selectedStaff,
        serviceId: formData.selectedService,
        date: selectedDate,
        startTime: formData.selectedTime,
        firstName: formData.customerFirstName,
        lastName: formData.customerLastName,
        email: formData.customerEmail,
        phone: formData.customerPhone,
        notes: formData.notes,
        gdprConsent: true, // Předpokládáme souhlas
      };

      const response = await bookingsWriteApi.createBooking1({ appCreateBookingRequest: createRequest });
      
      console.log('Rezervace vytvořena:', response.data);
      
      // Zavolej callback s daty rezervace
      if (onFinish) {
        onFinish(response.data);
      }
      
      onClose();
    } catch (error) {
      console.error('Chyba při vytváření rezervace:', error);
      alert('Chyba při vytváření rezervace. Zkuste to prosím znovu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filtrujeme služby podle vybraného zaměstnance
  const filteredServices = useMemo(() => {
    if (!formData.selectedStaff) {
      return servicesList;
    }
    
    // Použijeme služby zaměstnance, pokud jsou načtené
    if (employeeServices.length > 0) {
      return employeeServices.map(empService => ({
        id: empService.serviceId,
        name: empService.serviceName,
        price: empService.price, // Použijeme novou cenu z API
        duration: empService.duration, // Použijeme nové trvání z API
      }));
    }
    
    // Fallback na všechny služby, pokud se služby zaměstnance nenačetly
    return servicesList;
  }, [servicesList, formData.selectedStaff, employeeServices]);

  const selectedService = filteredServices.find(service => service.id === formData.selectedService);
  const totalPrice = selectedService?.price || 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCalendar} className="text-green-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Nová rezervace</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Datum */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-sm text-gray-600">
              <FontAwesomeIcon icon={faCalendar} className="mr-2" />
              <span>Datum rezervace: {dayjs(selectedDate).format('D. MMMM YYYY')}</span>
            </div>
          </div>

          {/* Zákazník */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-600" />
              Informace o zákazníkovi
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Jméno *
                  </label>
                  <input
                    type="text"
                    name="customerFirstName"
                    value={formData.customerFirstName}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Jan"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Příjmení *
                  </label>
                  <input
                    type="text"
                    name="customerLastName"
                    value={formData.customerLastName}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Novák"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FontAwesomeIcon icon={faPhone} className="mr-1" />
                  Telefon *
                </label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="+420 123 456 789"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                Email *
              </label>
              <input
                type="email"
                name="customerEmail"
                value={formData.customerEmail}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="jan.novak@example.com"
              />
            </div>
          </div>

          {/* Služba a zaměstnanec */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faClock} className="mr-2 text-purple-600" />
              Detaily rezervace
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zaměstnanec *
                </label>
                <select
                  name="selectedStaff"
                  value={formData.selectedStaff}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Vyberte zaměstnance</option>
                  {staffList.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Služba *
                  {formData.selectedStaff && (
                    <span className="text-xs text-blue-600 ml-2">
                      (filtrováno podle zaměstnance)
                    </span>
                  )}
                </label>
                <select
                  name="selectedService"
                  value={formData.selectedService}
                  onChange={handleInputChange}
                  required
                  disabled={isLoadingEmployeeServices}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">
                    {isLoadingEmployeeServices 
                      ? 'Načítání služeb...'
                      : formData.selectedStaff 
                        ? `Vyberte službu pro ${staffList.find(s => s.id === formData.selectedStaff)?.name || 'zaměstnance'}`
                        : 'Nejdříve vyberte zaměstnance'
                    }
                  </option>
                  {filteredServices.length === 0 ? (
                    <option value="" disabled>
                      Žádné služby nejsou dostupné pro vybraného zaměstnance
                    </option>
                  ) : (
                    filteredServices.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name} - {service.price} Kč ({service.duration} min)
                      </option>
                    ))
                  )}
                </select>
                {formData.selectedStaff && (
                  <div className="text-xs text-gray-500 mt-1">
                    {isLoadingEmployeeServices 
                      ? 'Načítání služeb zaměstnance...'
                      : `Dostupné služby: ${filteredServices.length} z ${servicesList.length}`
                    }
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Čas rezervace *
              </label>
              <select
                name="selectedTime"
                value={formData.selectedTime}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Vyberte čas</option>
                {timeSlots.map(time => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              
              {/* Indikátor dostupnosti */}
              {formData.selectedTime && formData.selectedStaff && formData.selectedService && (
                <div className="mt-2 flex items-center space-x-2">
                  {availabilityCheck.isLoading ? (
                    <div className="flex items-center text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span className="text-sm">Kontrola dostupnosti...</span>
                    </div>
                  ) : availabilityCheck.isAvailable !== null ? (
                    <div className={`flex items-center text-sm ${
                      availabilityCheck.isAvailable ? 'text-green-600' : 'text-red-600'
                    }`}>
                      <FontAwesomeIcon 
                        icon={availabilityCheck.isAvailable ? faCheckCircle : faExclamationTriangle} 
                        className="mr-2" 
                      />
                      <span>{availabilityCheck.message}</span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Cena */}
            {selectedService && (
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Celková cena:</span>
                  <div className="flex items-center text-lg font-bold text-green-600">
                    <FontAwesomeIcon icon={faEuroSign} className="mr-1" />
                    {totalPrice} Kč
                  </div>
                </div>
                {selectedService.duration && (
                  <div className="text-sm text-gray-600 mt-1">
                    Doba trvání: {selectedService.duration} minut
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Poznámky
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Speciální požadavky, alergie, atd."
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !availabilityCheck.isAvailable}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Vytváření...
                </>
              ) : (
                'Vytvořit rezervaci'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBookingModal;
